import {Injectable} from "@angular/core";
import {Observable} from "rxjs/Observable";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {DynForm} from "../domain/meta/dyn-form";
import {GenericService} from "../../core/commons/generic.service";
import {QueryOptions} from "../../core/commons/query.options";
import {AuthService} from "../../core/security/auth.service";
import {FormUtils} from "../domain/meta/form-utils";
import {DynFieldType} from "../domain/meta/dyn-field-type";
import {SelectFieldOptionsService} from "./select-field-options-service.service";
import {DynSelectFieldOptions} from "../domain/meta/fields/select/dyn-select-field-options";
import {RequestMethod} from "@angular/http";
import "rxjs/add/operator/take";
import "rxjs/add/observable/combineLatest";
import "rxjs/add/observable/of";
import "rxjs/add/observable/throw";
import "rxjs/add/operator/concatMap";
import {PaginationPage} from "app/core/commons/pagination-page";
import {FormImage} from "../domain/meta/form-image";
import {of} from "rxjs/observable/of";

@Injectable()
export class FormService extends GenericService<DynForm> {
    public options$ = null;
    public subforms$ = null;
    private _images: FormImage[];

    constructor(
        authService: AuthService,
        private optionsService: SelectFieldOptionsService
    ) {
        super(authService);
    }

    public get subforms(): Observable<Array<DynForm>> {
        if (this.subforms$ === null) {
            this.subforms$ = new BehaviorSubject<Array<DynForm>>(null);
            this.getAllSubforms()
                .take(1)
                .subscribe(s => this.subforms$.next(s));
        }
        return this.subforms$.asObservable().filter(s => s !== null);
    }

    public get options(): Observable<Array<DynSelectFieldOptions<any>>> {
        if (this.options$ === null) {
            this.options$ = new BehaviorSubject<Array<DynSelectFieldOptions<any>>>(
                null
            );
            this.optionsService
                .getAll()
                .take(1)
                .subscribe(o => this.options$.next(o));
        }
        return this.options$.asObservable().filter(o => o !== null);
    }

    // tslint:disable-next-line: member-ordering
    static getDynFieldTypes(): Observable<DynFieldType[]> {
        return Observable.of([
            {id: 0, name: "Texto Corto", fieldTypeName: "text", icon: "short_text"},
            {
                id: 1,
                name: "Cuadro de Texto",
                fieldTypeName: "textarea",
                icon: "reorder"
            },
            {
                id: 2,
                name: "Casilla de Verificación",
                fieldTypeName: "boolean",
                icon: "check_box"
            },
            {
                id: 3,
                name: "Lista Desplegable",
                fieldTypeName: "select",
                icon: "arrow_drop_down_circle"
            },
            {id: 4, name: "Fecha", fieldTypeName: "date", icon: "today"},
            //  {id: 5, name: 'Hora', fieldTypeName: 'time', icon: 'access_time'},
            {
                id: 6,
                name: "Sub Formulario",
                fieldTypeName: "subform",
                icon: "widgets"
            },
            {
                id: 7,
                name: "Texto Estático",
                fieldTypeName: "static-text",
                icon: "subject"
            },
            {
                id: 8,
                name: "Texto a completar",
                fieldTypeName: "fillableText",
                icon: "receipt"
            },
            {
                id: 9,
                name: "Campo para firma",
                fieldTypeName: "signature",
                icon: "gesture"
            }
        ]);
    }

    getDefaultQueryOptions(): QueryOptions {
        return new QueryOptions();
    }

    /*
          public get(id: number): Observable<DynForm> {
              return this.doRequest({
                  url: this.baseUrl + this.getResourcePath() + '/' + id,
                  method: RequestMethod.Get
              })
                  .concatMap(res => this.valueToForm(res.json()));
          }
      */

    public save(form: DynForm): Observable<DynForm> {
        return form.getId() ? this.update(form) : this.create(form);
    }

    public getAllNotSubforms(): Observable<Array<DynForm>> {
        return this.doRequest({
            url: this.baseUrl + this.getResourcePath() + "/notsubform",
            method: RequestMethod.Get
        }).map(res => res.json().map(f => FormUtils.parseFlatForm(f)));
    }

    public getAllSubforms(): Observable<Array<DynForm>> {
        return this.doRequest({
            url: this.baseUrl + this.getResourcePath() + "/subform",
            method: RequestMethod.Get
        }).map(res => res.json().map(f => FormUtils.parseSubform(f)));
    }

    public publish(form: DynForm) {
        return this.doRequest({
            url: this.baseUrl + this.getResourcePath() + "/publish",
            method: RequestMethod.Post,
            body: JSON.stringify(form)
        }).map(res => FormUtils.parseFlatForm(res.json()));
    }

    public disable(form: DynForm) {
        return this.doRequest({
            url: this.baseUrl + this.getResourcePath() + "/disable",
            method: RequestMethod.Post,
            body: JSON.stringify(form)
        }).map(res => FormUtils.parseFlatForm(res.json()));
    }

    public createNewVersion(form: DynForm) {
        return this.doRequest({
            url: this.baseUrl + this.getResourcePath() + "/new_version",
            method: RequestMethod.Post,
            body: JSON.stringify(form)
        }).concatMap(res => this.mapValueToEntity(res.json()));
    }

    public valueToForm(value: any): Observable<DynForm> {
        return Observable.combineLatest(this.options, this.subforms, (op, sf) =>
            FormUtils.parseForm(value, op, sf)
        )
    }

    public valuesToForms(values: Array<Object>): Observable<Array<DynForm>> {
        return Observable.combineLatest(this.options, this.subforms, (op, sf) =>
            values.map(f => FormUtils.parseForm(f, op, sf))
        );
    }

    protected valueToEntityGet(value: any): Observable<DynForm> {
        return this.valueToForm(value);
    }

    protected getResourcePath(): string {
        return "/forms";
    }

    protected valueToEntity(value: Object): DynForm {
        return FormUtils.parseFlatForm(value);
    }

    public pagedSearch(
        reference: string,
        name: string,
        ministry: any,
        state: string, // Publicado, Deshabilitado
        subform: string,
        queryOptions: QueryOptions = this.getDefaultQueryOptions()
    ): Observable<PaginationPage<DynForm>> {
        const ro = queryOptions.getQueryRequestOptions().merge({
            url: this.baseUrl + this.getResourcePath() + "/page"
        });
        if (reference) {
            ro.params.append("reference", reference);
        }
        if (name) {
            ro.params.append("name", name);
        }
        if (ministry) {
            ro.params.append("ministry", ministry);
        }
        if (state) {
            ro.params.append("state", state);
        }
        ro.params.append("subform", subform);

        return this.doRequest(ro)
            .map(res => res.json())
            .concatMap(json =>
                Observable.from(json.content)
                    .concatMap(value => this.mapValueToEntityQuery(value))
                    .toArray()
                    .map(content => new PaginationPage({...json, content: content}))
            );
    }


    public getLastImages(quantity: number = 5) {
        if (this._images && this._images.length > 0) {
            return of(this._images)
        }
        return this.doRequest({
            url: this.baseUrl + this.getResourcePath() + "/last_images?quantity=" + quantity,
            method: RequestMethod.Get,
        }).map(res => {
            const array = (res.json() as Array<FormImage>).map(v => new FormImage(v))
            this._images = array;
            return array;
        });
    }

    public addFormImageToCache(image: FormImage) {
        if (image && image.data && image.data.length > 0) {
            this._images.unshift(image);
            if (this._images.length > 5) {
                this._images.pop();
            }
        }

    }
}



// WEBPACK FOOTER //
// ./src/app/forms/services/form.service.ts